// PARITY-0: ⌘K's commands as a registry (SEARCH-NOTIF's "Commands" group: the actions people look
// for by name — "Invite person", "New seat", …). A feature registers its command here instead of
// editing the shell; the company shell builds one `ShellActionContext` and the palette lists the
// commands whose `when` holds, sorted by order then id, as `CommandPage` rows (command-menu.tsx).
// Today's commands are registered in default-commands.ts. The Create menu (create-registry.ts)
// shares the context.
import type { LucideIcon } from "lucide-react";
import type { MeResponse } from "../api/types";
import { orderedRegistry } from "../extensions/ordered-registry";
import type { CompanyAccess } from "../features/company";
import type { QuickAddKind } from "../features/quick-add/quick-add-host";
import type { CommandPage } from "./command-menu";

/** What a shell action sees: who, where, and how to go somewhere (with the playhead). */
export type ShellActionContext = {
  me: MeResponse;
  company: CompanyAccess;
  /**
   * Opens a company route (`/c/$slug/…`) keeping the playhead; `search` adds the page's own
   * params (a command opening a dialog: `{ invite: "1" }`).
   */
  go: (to: string, search?: Record<string, string>) => void;
  /**
   * MODALS: opens a company quick add (invite, new seat, new metric) over the current page;
   * absent outside a company shell, where a command goes to the page instead.
   */
  quickAdd?: (kind: QuickAddKind) => void;
  /** The viewer opens Setup and manages the company (the Setup pages and their commands). */
  setupOpen: boolean;
  /** The add-ons the palette reads. */
  features: { kpi: boolean; memory: boolean };
};

export type ShellCommand = {
  id: string;
  title: string;
  /** Lower first; ties by id. */
  order: number;
  icon?: LucideIcon;
  /** The palette heading (default "Commands"). */
  group?: string;
  /** More words the command is found by. */
  keywords?: string;
  when: (ctx: ShellActionContext) => boolean;
  run: (ctx: ShellActionContext) => void;
};

const commands = orderedRegistry<ShellCommand>("command");

/** Adds a ⌘K command; throws when its id is taken. Returns the unregister function. */
export function registerCommand(command: ShellCommand): () => void {
  return commands.register(command);
}

/** Every registered command, sorted by order, then id. */
export function registeredCommands(): ShellCommand[] {
  return commands.list();
}

/** The commands this context shows, as the palette's rows (key = id, label = title). */
export function commandPages(ctx: ShellActionContext): CommandPage[] {
  return commands
    .list()
    .filter((c) => c.when(ctx))
    .map((c) => ({
      key: c.id,
      label: c.title,
      group: c.group ?? "Commands",
      ...(c.icon ? { icon: c.icon } : {}),
      ...(c.keywords ? { keywords: c.keywords } : {}),
      run: () => c.run(ctx),
    }));
}
